import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageCircle, Send, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', subject: 'General Inquiry', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      const api = (await import('../services/api')).default;
      await api.post('/contact-us', formData);
      setSent(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: 'General Inquiry', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try contacting us directly.');
    } finally {
      setSending(false);
    }
  };

  const subjects = ['General Inquiry', 'Trip Planning Help', 'Feedback', 'Complaint', 'Partnership'];

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="contact-page">
      {/* Premium Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="w-14 h-14 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Contact Us</h1>
            <p className="text-white/80">We're here to help you plan your perfect trip</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: Contact Info */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
              className="space-y-5">
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-7">
                <h2 className="font-serif text-xl text-ocean mb-5">Customer Support</h2>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3.5 bg-ocean/8 rounded-xl border border-ocean/10">
                    <div className="w-10 h-10 bg-gradient-to-br from-ocean to-sky rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4.5 h-4.5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-ocean mb-1 text-sm">Phone</h3>
                      <a href="tel:+61401050457" className="text-ocean/80 hover:text-ocean hover:underline block text-sm" data-testid="phone-1">+61 401 050 457</a>
                      <a href="tel:+61424024078" className="text-ocean/80 hover:text-ocean hover:underline block text-sm" data-testid="phone-2">+61 424 024 078</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3.5 bg-green-500/8 rounded-xl border border-green-500/10">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-4.5 h-4.5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-ocean mb-1 text-sm">WhatsApp</h3>
                      <a href="https://api.whatsapp.com/send?phone=61401050457" target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline block text-sm" data-testid="whatsapp-1">+61 401 050 457</a>
                      <a href="https://api.whatsapp.com/send?phone=61424024078" target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline block text-sm" data-testid="whatsapp-2">+61 424 024 078</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3.5 bg-gold/8 rounded-xl border border-gold/10">
                    <div className="w-10 h-10 bg-gradient-to-br from-gold to-gold-light rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4.5 h-4.5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-ocean mb-1 text-sm">Email</h3>
                      <a href="mailto:triplea@tripleabooking.com" className="text-gold hover:underline text-sm" data-testid="email-link">triplea@tripleabooking.com</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 text-center border border-white/20">
                <img src="/logo.jpg" alt="Triple A Travel Solutions" className="w-12 h-12 rounded-xl mx-auto mb-3 object-contain bg-white/90 p-1" />
                <h3 className="font-serif text-lg text-white mb-1">Triple A Travel Solutions</h3>
                <p className="text-white/60 text-xs">Awake. Aware. Arise.</p>
                <p className="text-white/40 text-[10px] mt-3">&copy; {new Date().getFullYear()} Triple A Travel Solutions. All rights reserved.</p>
              </div>
            </motion.div>

            {/* Right: Write to Us form */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-7" data-testid="write-to-us-form">
                <h2 className="font-serif text-xl text-ocean mb-1">Write to Us</h2>
                <p className="text-ocean/60 text-sm mb-5">Have a question, feedback, or complaint? Send us a message.</p>

                {sent ? (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-10">
                    <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                    <h3 className="font-serif text-xl text-ocean mb-2">Message Sent!</h3>
                    <p className="text-ocean/60 text-sm mb-4">Thank you for reaching out. We'll respond within 24 hours.</p>
                    <Button variant="outline" onClick={() => setSent(false)}
                      className="border-ocean/30 text-ocean hover:bg-ocean/10" data-testid="send-another-btn">
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3.5">
                    <div>
                      <Label className="text-ocean/80 text-sm">Your Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="John Doe"
                        className="mt-1 h-10 border-ocean/20 focus:border-ocean"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-ocean/80 text-sm">Email Address *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="john@example.com"
                        className="mt-1 h-10 border-ocean/20 focus:border-ocean"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-ocean/80 text-sm">Subject</Label>
                      <select
                        value={formData.subject}
                        onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-ocean/20 text-sm focus:outline-none focus:ring-2 focus:ring-ocean/40 bg-white"
                        data-testid="contact-subject-select"
                      >
                        {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-ocean/80 text-sm">Message *</Label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Tell us how we can help..."
                        rows={4}
                        className="mt-1 w-full p-3 rounded-md border border-ocean/20 text-sm focus:outline-none focus:ring-2 focus:ring-ocean/40 resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>
                    <Button type="submit" disabled={sending}
                      className="w-full h-11 bg-gradient-to-r from-ocean to-sky text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                      data-testid="contact-submit-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      {sending ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
